import { Box, Button, Typography } from '@mui/material';
import { Copy } from '@phosphor-icons/react';
import React, { useCallback } from 'react';
import { NotificationType } from '../../../../store/storeNotifications';
import type { PullPanelExtrasProps } from '../../types';

const ZapierPullPanelExtras: React.FC<PullPanelExtrasProps> = ({
  resolvedValues,
  selectedObject,
  disabled,
  isDark,
  setNotification,
}) => {
  const webhookUrlForCopy =
    selectedObject === 'webhook_listener' && typeof resolvedValues.webhookUrl === 'string'
      ? resolvedValues.webhookUrl
      : '';

  const handleCopyWebhookUrl = useCallback(() => {
    if (!webhookUrlForCopy) return;
    navigator.clipboard.writeText(webhookUrlForCopy).then(
      () => {
        setNotification({ type: NotificationType.Success, message: 'Webhook URL copied to clipboard' });
      },
      () => {},
    );
  }, [webhookUrlForCopy, setNotification]);

  if (selectedObject !== 'webhook_listener') return null;

  return (
    <>
      {webhookUrlForCopy && (
        <Button
          className="nodrag"
          size="small"
          variant="outlined"
          startIcon={<Copy size={16} />}
          onClick={handleCopyWebhookUrl}
          disabled={disabled}
          sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
        >
          Copy webhook URL
        </Button>
      )}
      <Box
        sx={{
          mt: 1,
          p: 1.5,
          borderRadius: 1,
          bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: isDark ? '#D0D5DD' : '#344054',
            display: 'block',
            mb: 0.5,
          }}
        >
          How to connect Zapier to this node
        </Typography>
        <Typography
          component="ol"
          variant="caption"
          sx={{
            color: isDark ? '#98A2B3' : '#667085',
            pl: 2,
            m: 0,
            '& li': { mb: 0.25 },
          }}
        >
          <li>Copy the Webhook URL above and paste it in your Zap.</li>
          <li>In Zapier.com create a Zap: choose a trigger (e.g. New Form Submission, New Row in Google Sheets).</li>
          <li>Add action &quot;Webhooks by Zapier&quot; → POST. Set the URL to the copied Webhook URL.</li>
          <li>Map the trigger fields to the body. When the Zap runs, data will be sent here.</li>
          <li>Use &quot;Load last received&quot; to preview the latest payload, or run the flow to use it.</li>
        </Typography>
      </Box>
    </>
  );
};

export default ZapierPullPanelExtras;
